import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Check, Copy, LifeBuoy, Mail } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { csrfToken } from "@/lib/orderActions"

const SUPPORT_EMAIL = "support@dibbly.com"
const TOPICS = [
  "Membership or billing",
  "Locked-in pricing",
  "Account access",
  "Something else",
]

export default function InsiderSupportCard() {
  const { flash } = usePage().props
  const [open, setOpen] = useState(false)
  const [topic, setTopic] = useState(TOPICS[0])
  const [message, setMessage] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const valid = message.trim().length >= 10 && message.trim().length <= 2000

  function submit(event) {
    event.preventDefault()
    if (!valid) return

    setSubmitting(true)
    router.post(
      "/insider/support",
      { topic, message },
      {
        headers: { "X-CSRF-Token": csrfToken() },
        preserveScroll: true,
        onSuccess: (page) => {
          if (page.props.flash?.notice) {
            setOpen(false)
            setMessage("")
          }
        },
        onFinish: () => setSubmitting(false),
      }
    )
  }

  async function copyEmail() {
    await navigator.clipboard.writeText(SUPPORT_EMAIL)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <>
      <section className="mt-[16px] flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center">
        <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-secondary text-primary">
          <LifeBuoy className="size-5" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[15px] font-bold text-foreground">Need help with Insider?</h2>
          <p className="mt-1 text-[12.5px] leading-5 text-ink-3">
            Send an account-level question here, or email{" "}
            <a className="font-semibold text-primary underline underline-offset-2" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="outline" size="sm" onClick={copyEmail}>
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? "Copied" : "Copy email"}
          </Button>
          <Button type="button" size="sm" onClick={() => setOpen(true)}>
            <Mail className="size-3.5" />
            Contact support
          </Button>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-0 p-0 sm:max-w-[540px]">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle className="font-display text-lg font-bold">Contact Insider support</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="flex flex-col gap-4 px-6 py-5">
            {flash?.alert && (
              <p role="alert" className="rounded-lg bg-[var(--red-soft)] px-3 py-2 text-sm font-medium text-primary">
                {flash.alert}
              </p>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="insider-support-topic">Topic</Label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger id="insider-support-topic">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TOPICS.map((option) => (
                    <SelectItem key={option} value={option}>{option}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="insider-support-message">How can we help?</Label>
              <Textarea
                id="insider-support-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={7}
                maxLength={2000}
                placeholder="Tell us what you need help with."
              />
              <span className="text-right text-[11px] text-ink-3">{message.trim().length}/2,000</span>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !valid}>
                {submitting ? "Sending…" : "Send message"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
